import React from 'react';

function RiskDisclaimer() {
  const handleBack = () => {
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className="min-h-screen bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-gray-800 rounded-lg shadow-xl p-8">
          {/* Back Button */}
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="flex items-center text-blue-400 hover:text-blue-300 transition-colors"
            >
              <span className="mr-2">←</span>
              Back
            </button>
          </div>

          <div className="text-center mb-8">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-4xl font-bold text-red-400 mb-4">
              RISK DISCLAIMER
            </h1>
            <p className="text-xl text-red-300">
              READ THIS CAREFULLY BEFORE USING ACTUVO.AI
            </p>
          </div>
          
          <div className="text-gray-300 space-y-6">
            <div className="bg-red-900 border-2 border-red-600 p-6 rounded-lg">
              <h2 className="text-2xl font-bold text-red-100 mb-4 text-center">
                🚨 HIGH RISK INVESTMENT WARNING 🚨
              </h2>
              <p className="text-red-100 text-lg font-semibold text-center">
                TRADING FINANCIAL INSTRUMENTS INVOLVES SUBSTANTIAL RISK OF LOSS
              </p>
            </div>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. General Investment Risks</h2>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-red-800 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-red-100 mb-2">⚠️ Capital Loss Risk</h3>
                  <p className="text-red-100 text-sm">
                    You may lose some or ALL of your invested capital. Never invest money you cannot afford to lose.
                  </p>
                </div>
                
                <div className="bg-orange-800 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-orange-100 mb-2">📈 Market Volatility</h3>
                  <p className="text-orange-100 text-sm">
                    Financial markets are highly volatile. Prices can change rapidly and unpredictably.
                  </p>
                </div>
                
                <div className="bg-yellow-800 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-yellow-100 mb-2">🎯 No Guarantees</h3>
                  <p className="text-yellow-100 text-sm">
                    Past performance does not guarantee future results. All investments carry risk.
                  </p>
                </div>
                
                <div className="bg-purple-800 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-purple-100 mb-2">⚡ Leverage Risk</h3>
                  <p className="text-purple-100 text-sm">
                    Leveraged trading amplifies both potential profits and losses significantly.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Automated Trading Risks</h2>
              
              <div className="bg-gray-700 p-6 rounded-lg mb-4">
                <h3 className="text-xl font-semibold text-white mb-3">🤖 Algorithm Limitations</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li><strong>AI Imperfection:</strong> Our algorithms are not perfect and can make incorrect predictions</li>
                  <li><strong>Market Changes:</strong> Algorithms may not adapt quickly to sudden market changes</li>
                  <li><strong>Data Dependency:</strong> Trading decisions are based on historical data which may not predict future performance</li>
                  <li><strong>System Errors:</strong> Technical glitches or bugs in the algorithm can cause unexpected losses</li>
                </ul>
              </div>

              <div className="bg-gray-700 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-white mb-3">⚙️ Technical Risks</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li><strong>Connectivity Issues:</strong> Internet or API failures may prevent trades from executing</li>
                  <li><strong>Exchange Problems:</strong> Hyperliquid or other exchange issues beyond our control</li>
                  <li><strong>System Downtime:</strong> Maintenance or technical failures may interrupt service</li>
                  <li><strong>API Limitations:</strong> Exchange API restrictions or rate limits may affect trading</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Regulatory and Legal Risks</h2>
              
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Regulatory Changes:</strong> Financial regulations may change, affecting trading or the service</li>
                <li><strong>Tax Implications:</strong> Trading profits may be subject to taxes in your jurisdiction</li>
                <li><strong>Legal Restrictions:</strong> Automated trading may not be legal in all jurisdictions</li>
                <li><strong>Exchange Regulations:</strong> Hyperliquid may have specific rules about automated trading</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Service-Specific Warnings</h2>
              
              <div className="space-y-4">
                <div className="bg-blue-900 border border-blue-700 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-100 mb-2">🔄 Continuous Updates</h3>
                  <p className="text-blue-100">
                    actuvo.ai is actively maintained. Features may change, bugs may exist, and service interruptions may occur during updates.
                  </p>
                </div>

                <div className="bg-indigo-900 border border-indigo-700 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-indigo-100 mb-2">🔑 API Key Security</h3>
                  <p className="text-indigo-100">
                    You are responsible for the security of your Hyperliquid API credentials. Compromised keys could lead to unauthorized trading.
                  </p>
                </div>

                <div className="bg-teal-900 border border-teal-700 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-teal-100 mb-2">💰 Subscription Costs</h3>
                  <p className="text-teal-100">
                    Monthly subscription fees are separate from any trading losses. You pay for the service regardless of trading performance.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Risk Management Recommendations</h2>
              
              <div className="bg-green-900 border border-green-700 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-green-100 mb-4">✅ Best Practices</h3>
                <ul className="list-disc list-inside space-y-2 text-green-100">
                  <li><strong>Start Small:</strong> Begin with small amounts you can afford to lose</li>
                  <li><strong>Diversify:</strong> Don't put all your money in automated trading</li>
                  <li><strong>Monitor Regularly:</strong> Check your trades and account balance frequently</li>
                  <li><strong>Set Limits:</strong> Use stop-losses and position size limits</li>
                  <li><strong>Stay Informed:</strong> Keep up with market news and conditions</li>
                  <li><strong>Seek Advice:</strong> Consult financial advisors before making major investments</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Performance Disclaimers</h2>
              
              <div className="space-y-4">
                <div className="bg-yellow-900 border border-yellow-700 p-4 rounded-lg">
                  <p className="text-yellow-100 font-semibold">
                    📊 Past performance results shown in marketing materials, backtests, or user testimonials 
                    do not guarantee future performance and should not be the sole basis for investment decisions.
                  </p>
                </div>

                <div className="bg-orange-900 border border-orange-700 p-4 rounded-lg">
                  <p className="text-orange-100 font-semibold">
                    🎲 Hypothetical or simulated performance results have inherent limitations and may not reflect 
                    actual trading conditions, fees, or market impact.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Your Acknowledgment</h2>
              
              <div className="bg-red-900 border-2 border-red-600 p-6 rounded-lg">
                <p className="text-red-100 font-semibold text-lg mb-4">
                  By using actuvo.ai, you acknowledge that:
                </p>
                <ul className="list-disc list-inside space-y-2 text-red-100">
                  <li>You understand the substantial risks involved in automated trading</li>
                  <li>You may lose some or all of your invested capital</li>
                  <li>You are using the service entirely at your own risk</li>
                  <li>You have read and understood all risk warnings</li>
                  <li>You are financially capable of bearing potential losses</li>
                  <li>You will not hold actuvo.ai liable for any trading losses</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Seek Professional Advice</h2>
              
              <div className="bg-blue-900 border border-blue-700 p-6 rounded-lg">
                <p className="text-blue-100 text-lg">
                  <strong>⚖️ Professional Consultation Recommended:</strong>
                </p>
                <p className="text-blue-100 mt-2">
                  Before using automated trading services, consider consulting with qualified financial advisors, 
                  tax professionals, or legal counsel who can provide personalized advice based on your specific 
                  financial situation and risk tolerance.
                </p>
              </div>
            </section>

            <div className="mt-8 p-6 bg-red-900 border-2 border-red-600 rounded-lg text-center">
              <h3 className="text-2xl font-bold text-red-100 mb-4">🛑 FINAL WARNING 🛑</h3>
              <p className="text-red-100 text-xl font-semibold">
                ONLY INVEST MONEY YOU CAN AFFORD TO LOSE
              </p>
              <p className="text-red-100 mt-4">
                If you cannot afford to lose the money you're planning to invest, 
                DO NOT USE THIS SERVICE.
              </p>
            </div>

            <div className="mt-6 text-center">
              <p className="text-gray-400 text-sm">
                This risk disclaimer was last updated on January 8, 2025
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RiskDisclaimer;